import { useTranslation } from 'react-i18next'
import { MapPin, ExternalLink, Clock, Globe, Wifi } from 'lucide-react'
import { PageVisit } from '../../types/analytics'
import { formatFromUTC } from '@/utils/function'

interface JourneyTimelineProps {
    pageVisits: PageVisit[]
    uniqueOrigins: string[]
}

// Extract path from URL for display
const getPathFromUrl = (url: string): string => {
    try {
        const urlObj = new URL(url)
        return urlObj.pathname || '/'
    } catch {
        return url
    }
}

// Get origin color based on index
const getOriginColor = (origin: string, uniqueOrigins: string[]): string => {
    const colors = [
        'bg-blue-500',
        'bg-green-500',
        'bg-orange-500',
        'bg-pink-500',
        'bg-cyan-500',
    ]
    const index = uniqueOrigins.indexOf(origin)
    return colors[index % colors.length]
}

const JourneyTimeline: React.FC<JourneyTimelineProps> = ({
    pageVisits,
    uniqueOrigins,
}) => {
    const { t } = useTranslation()

    if (!pageVisits || pageVisits.length === 0) {
        return (
            <div className="text-center py-4 text-gray-400 text-sm">
                {t('No page visits recorded')}
            </div>
        )
    }

    return (
        <div className="space-y-1">
            {/* Cross-site indicator */}
            {uniqueOrigins.length > 1 && (
                <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-amber-50 rounded-lg">
                    <MapPin size={12} className="text-amber-600" />
                    <span className="text-xs text-amber-700">
                        {t('Cross-site visitor')}: {uniqueOrigins.length}{' '}
                        {t('sites')}
                    </span>
                    <div className="flex gap-1 ml-auto">
                        {uniqueOrigins.map((origin) => (
                            <span
                                key={origin}
                                className={`w-2 h-2 rounded-full ${getOriginColor(origin, uniqueOrigins)}`}
                                title={origin}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Timeline */}
            <div className="relative">
                {pageVisits.map((visit, index) => {
                    const isFirst = index === 0
                    const isLast = index === pageVisits.length - 1

                    return (
                        <div
                            key={`${visit.url}-${visit.createdAt}`}
                            className="relative flex gap-3 pb-4 last:pb-0"
                        >
                            {/* Timeline line */}
                            {!isLast && (
                                <div className="absolute left-[7px] top-4 w-0.5 h-full bg-gray-200" />
                            )}

                            {/* Timeline dot */}
                            <div
                                className={`relative z-10 flex-shrink-0 w-4 h-4 rounded-full border-2 border-white shadow-sm ${
                                    isFirst
                                        ? 'bg-green-500'
                                        : getOriginColor(
                                              visit.origin,
                                              uniqueOrigins
                                          )
                                }`}
                                title={visit.origin}
                            />

                            {/* Content */}
                            <div className="flex-1 min-w-0 -mt-0.5">
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">
                                            {visit.title ||
                                                getPathFromUrl(visit.url)}
                                        </p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-xs text-gray-400 truncate">
                                                {getPathFromUrl(visit.url)}
                                            </span>
                                            {uniqueOrigins.length > 1 && (
                                                <span className="text-xs text-gray-400">
                                                    • {visit.origin}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <a
                                        href={visit.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                                    >
                                        <ExternalLink
                                            size={12}
                                            className="text-gray-400"
                                        />
                                    </a>
                                </div>

                                {/* Timestamp */}
                                <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                                    <Clock size={10} />
                                    <span>
                                        {formatFromUTC(
                                            visit.createdAt,
                                            'HH:mm:ss'
                                        )}
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span>
                                        {formatFromUTC(
                                            visit.createdAt,
                                            'DD/MM/YYYY'
                                        )}
                                    </span>
                                </div>

                                {/* Visitor Info */}
                                {(visit.visitorIp || visit.visitorCountry) && (
                                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1 text-xs text-gray-400">
                                        {visit.visitorIp && (
                                            <span className="flex items-center gap-1">
                                                <Wifi size={10} />
                                                {visit.visitorIp}
                                            </span>
                                        )}
                                        {(visit.visitorCity ||
                                            visit.visitorCountry) && (
                                            <span className="flex items-center gap-1">
                                                <Globe size={10} />
                                                {[
                                                    visit.visitorCity,
                                                    visit.visitorCountry,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Summary */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                <span>
                    {pageVisits.length} {t('pages visited')}
                </span>
                {pageVisits.length >= 2 && (
                    <span>
                        {formatFromUTC(pageVisits[0].createdAt, 'HH:mm')} →{' '}
                        {formatFromUTC(
                            pageVisits[pageVisits.length - 1].createdAt,
                            'HH:mm'
                        )}
                    </span>
                )}
            </div>
        </div>
    )
}

export default JourneyTimeline
